// MSG91 OTP Widget loader and helpers.
// Docs: https://msg91.com/help/sendotp/how-to-integrate-the-new-login-with-otp-widget
// We use the Web SDK in "Custom UI" mode (exposeMethods: true) so the widget
// is invisible — we control the UI and call window.sendOtp / window.verifyOtp.

const WIDGET_ID = process.env.REACT_APP_MSG91_WIDGET_ID;
const TOKEN_AUTH = process.env.REACT_APP_MSG91_TOKEN_AUTH;
const SCRIPT_SRC = "https://verify.msg91.com/otp-provider.js";

let scriptPromise = null;
let isInitialized = false;

export const isMsg91Enabled = () => Boolean(WIDGET_ID && TOKEN_AUTH);

/**
 * Loads the MSG91 otp-provider.js script (idempotent) and initializes the
 * widget in exposeMethods mode. Resolves once window.sendOtp/verifyOtp are
 * available. Rejects if the script can't load or widget is misconfigured.
 */
export const loadMsg91Widget = () => {
  if (!isMsg91Enabled()) {
    return Promise.reject(new Error("MSG91 widget not configured"));
  }
  if (isInitialized && typeof window.sendOtp === "function") {
    return Promise.resolve();
  }
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const configuration = {
      widgetId: WIDGET_ID,
      tokenAuth: TOKEN_AUTH,
      exposeMethods: true,
      // Global success/failure are no-ops; the per-call callbacks own the flow.
      success: () => {},
      failure: () => {},
    };

    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    const onReady = () => {
      // Some browsers fire onload before the script's IIFE attaches initSendOTP,
      // so poll briefly.
      const start = Date.now();
      const poll = () => {
        if (typeof window.initSendOTP === "function") {
          try {
            window.initSendOTP(configuration);
            // After init, sendOtp/verifyOtp should attach within a tick.
            const ready = () => {
              if (typeof window.sendOtp === "function") {
                isInitialized = true;
                resolve();
              } else if (Date.now() - start < 8000) {
                setTimeout(ready, 100);
              } else {
                reject(new Error("MSG91 widget did not expose methods"));
              }
            };
            ready();
          } catch (e) {
            reject(e);
          }
        } else if (Date.now() - start < 8000) {
          setTimeout(poll, 100);
        } else {
          reject(new Error("MSG91 widget script never exposed initSendOTP"));
        }
      };
      poll();
    };

    if (existing) {
      onReady();
      return;
    }

    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.onload = onReady;
    s.onerror = () => reject(new Error("Failed to load MSG91 widget script"));
    document.body.appendChild(s);
  });

  return scriptPromise;
};

// MSG91 expects the identifier WITHOUT the leading `+` but WITH the country code.
const formatIndianMobile = (mobile10) => `91${String(mobile10).replace(/\D/g, "").slice(-10)}`;

export const msg91SendOtp = (mobile10) =>
  new Promise((resolve, reject) => {
    if (typeof window.sendOtp !== "function") {
      reject(new Error("Widget not loaded yet"));
      return;
    }
    window.sendOtp(
      formatIndianMobile(mobile10),
      (data) => resolve(data),
      (err) => reject(err && err.message ? err : new Error(err?.message || "Failed to send OTP"))
    );
  });

export const msg91RetryOtp = () =>
  new Promise((resolve, reject) => {
    if (typeof window.retryOtp !== "function") {
      reject(new Error("Widget not loaded yet"));
      return;
    }
    window.retryOtp(
      null,
      (data) => resolve(data),
      (err) => reject(err && err.message ? err : new Error(err?.message || "Failed to resend OTP"))
    );
  });

export const msg91VerifyOtp = (otp) =>
  new Promise((resolve, reject) => {
    if (typeof window.verifyOtp !== "function") {
      reject(new Error("Widget not loaded yet"));
      return;
    }
    window.verifyOtp(
      Number(String(otp).replace(/\D/g, "")),
      (data) => {
        // Successful verification returns { type: 'success', message: '<jwt access-token>' }
        const accessToken = data?.message || data?.["access-token"] || data?.accessToken;
        if (!accessToken) {
          reject(new Error("Verification succeeded but no access token returned"));
          return;
        }
        resolve(accessToken);
      },
      (err) => reject(err && err.message ? err : new Error(err?.message || "Invalid OTP"))
    );
  });
